import {Injectable} from '@angular/core';
import {NavMenu, NavType} from "../models/nav-menu";
import {MatDrawer} from "@angular/material/sidenav";
import {UserRole, UserRoleString} from "../models/user";
import {CaseStatus, ProtestInfo} from "../models/protest";
import {Subject} from "rxjs";
import {CaseCompleteState, CompleteBtnData} from "../models/docket";

@Injectable({
  providedIn: 'root'
})
export class SidebarService {
  private _nav: NavMenu[] = [];
  private _addNav: NavMenu[] = [];
  private drawer!: MatDrawer;
  private _enabled : boolean = false;
  private _displayAdditionalNavs : boolean = false;
  private _addNavTitle!: string;
  private _displayEmailPref : boolean = false;
  autosize: boolean = false;
  completeButtonText = CaseCompleteState.NONE;
  protestInfo: ProtestInfo|undefined = undefined;

  private _emailSub = new Subject<void>();
  emailSub$ = this._emailSub.asObservable();
  private _completeSub = new Subject<CompleteBtnData>();
  completeSub$ = this._completeSub.asObservable();

  constructor() { }

  navType: NavType = NavType.NONE;
  navTypePrior: NavType = NavType.NONE;
  triggeringElement!: any;

  setDrawer(drawer: MatDrawer) {
    this.drawer = drawer;
  }

  toggle(isOpen?: boolean): void {
    this.drawer.toggle(isOpen);
  }

  getNav() {
    return this._nav;
  }

  addNav(nav : NavMenu) {
    this._nav.push(nav);
  }

  setNav(nav : NavMenu[]) {
    this._nav = nav;
    this.autosize = true;
    setTimeout(() => {
      this.autosize = false;

      let elem;
      if (this.triggeringElement && (elem = document.getElementById(this.triggeringElement))) {
        elem.focus();
      } else {
        let main = document.getElementById("mainContent");
        main?.focus();
      }
    }, 1);
  }

  getAdditionalNav() {
    return this._addNav;
  }

  addAdditionalNav(addNav : NavMenu) {
    this._addNav.push(addNav);
  }

  setAdditionalNav(addNav : NavMenu[]) {
    this._addNav = addNav;
    this.displayAdditionalNavs = true;
  }


  get addNavTitle(): string {
    return this._addNavTitle;
  }

  set addNavTitle(value: string) {
    this._addNavTitle = value;
  }

// called with and without profileInfo
  setNavByUser(userRole : UserRole, protestInfo ?: ProtestInfo) {
    if (!userRole) return;
    if ((this.navType == NavType.DOCKET || this.navType == NavType.DOCUMENT) && !protestInfo) return;

    this.triggeringElement = document.activeElement?.id;
    // console.log(document.activeElement);

    if (protestInfo)
      this.protestInfo = protestInfo;
    else
      this.protestInfo = undefined;

    let nav : NavMenu[] = [];
    this.setAdditionalNav([]);
    this.addNavTitle = "";
    this.displayEmailPref = false;
    this.completeButtonText = CaseCompleteState.NONE;

    switch (this.navType) {
      case NavType.PROFILE:
        this.addProfileNavs(nav, userRole);
        break;
      case NavType.DASHBOARD:
        this.addDashboardNavs(nav, userRole);
        break;
      case NavType.DOCKET:
        // @ts-ignore
        this.addDocketNavs(nav, userRole, protestInfo);
        break;
      case NavType.DOCUMENT:
        // @ts-ignore
        this.addDocumentNavs(nav, userRole, protestInfo);
        break;
      default: // NONE
        return;
    }
    this.setNav(nav);
  }

  get enabled(): boolean {
    return this._enabled;
  }

  set enabled(value: boolean) {
    this._enabled = value;
  }

  get displayAdditionalNavs(): boolean {
    return this._displayAdditionalNavs;
  }

  set displayAdditionalNavs(value: boolean) {
    this._displayAdditionalNavs = value;
  }

  get displayEmailPref(): boolean {
    return this._displayEmailPref;
  }

  set displayEmailPref(value: boolean) {
    this._displayEmailPref = value;
  }

  private addDashboardNavs(nav: NavMenu[], userRole: UserRole) {
    this.addActiveCases(nav, userRole);

    if (userRole === UserRole.GAO_Admin || userRole === UserRole.Protester) {
      nav.push({title:'File New Protest',routeToLoad:'/user/file-new-protest', id:'newProtest'});
    }

    if (userRole === UserRole.Protester) {
      nav.push({title:'Request to Intervene',routeToLoad:'/user/intervene', id:'reqIntervene'});
    }

    if (userRole === UserRole.Agency_Attorney) {
      nav.push({title:'Join a Case',routeToLoad:'/user/join-case', id:'joinCase'});
    }

    if (userRole === UserRole.GAO_Admin || userRole === UserRole.GAO_Supervisor || userRole === UserRole.GAO_Attorney) {
      nav.push({title:'Advanced Search',routeToLoad:'/user/advanced-search', id:'advSearch'});
    }

    //Admin Tools
    if (userRole == UserRole.GAO_Admin) {
      this.setAdditionalNav([
        {title:'Manage Agency Contacts',routeToLoad:'/admin/manage-agency-contacts', id:'mngAgency'},
        {title:'Manage GAO Users',routeToLoad:'/admin/manage-gao-users', id:'mngGao'},
        {title:'Remove Case',routeToLoad:'/admin/remove-case', id:'remCase'},
        {title:'Send System Notification',routeToLoad:'/admin/sys-note', id:'sysNote'},
        {title:'Edit Templates',routeToLoad:'/admin/edit-templates', id:'editTemplates'},
        {title:'Audit Reports',routeToLoad:'/admin/audit-reports', id:'audit'},
        {title:'Manage Accounts',routeToLoad:'/admin/user-search', id:'mngAccounts'}

      ]);
      this.addNavTitle = "Admin Tools";
    }
  }

  private addProfileNavs(nav: NavMenu[], userRole: UserRole) {
    this.addActiveCases(nav, userRole);

    nav.push({title:'Change Password',routeToLoad:'/user/change-pwd', id:'chgPass'});

    if (userRole === UserRole.Protester) {
      nav.push({title:'Change Username',routeToLoad:'/user/change-user', id:'chgUser'});
    } else {
      nav.push({title:'Change Security Questions',routeToLoad:'/user/change-sec-q', id:'chgSeqQ'});
    }
  }

  private addDocketNavs(nav: NavMenu[], userRole: UserRole, protestInfo: ProtestInfo) {
    this.addActiveCases(nav, userRole);
    this.addSubmitNewDoc(nav, userRole, protestInfo);
    this.addParties(nav, userRole, protestInfo);
    this.addAdvancedSearch(nav, userRole, protestInfo);
    this.addJoinUnjoin(nav, userRole, protestInfo);
    this.addEmailPref(nav, userRole, protestInfo);
    this.addReconEntitleCosts(userRole, protestInfo);
    this.addComplete(nav, userRole, protestInfo);
  }

  private addDocumentNavs(nav: NavMenu[], userRole: UserRole, protestInfo: ProtestInfo) {
    this.addActiveCases(nav, userRole);
    nav.push({title:'Case Docket Sheet',routeToLoad:'/' + UserRoleString[userRole] + '/case-docketsheet/' + protestInfo.a_No, id:'docket'});
    this.addSubmitNewDoc(nav, userRole, protestInfo);
    this.addParties(nav, userRole, protestInfo);
    this.addAdvancedSearch(nav, userRole, protestInfo);
    this.addJoinUnjoin(nav, userRole, protestInfo);
    // this.addEmailPref(nav, userRole, protestInfo);
    this.addReconEntitleCosts(userRole, protestInfo);
    this.addComplete(nav, userRole, protestInfo);
  }

  private addActiveCases(nav: NavMenu[], userRole: UserRole) {
    if (userRole === UserRole.GAO_Admin) {
      nav.push({title:'Unassigned Cases', routeToLoad:'/admin/dashboard', id:'unassigned'},
        {title:'Assigned Cases',routeToLoad:'/admin/dashboard/assigned', id:'assigned'});
    } else {
      nav.push({title:'Active Cases',routeToLoad:'/user/dashboard', id:'active'});
    }
  }

  private addSubmitNewDoc(nav: NavMenu[], userRole: UserRole, protestInfo: ProtestInfo) {
    const numberOfDays = protestInfo.public_decision_date ? this.differenceInDays(new Date(), new Date(protestInfo.public_decision_date)) : 0;
    let showSubmit = false;

    if (protestInfo.caseCompletionStatus.isZipCreated != true) {
      if (userRole === UserRole.GAO_Admin) {
        showSubmit = true;
      } else if (!protestInfo.viewOnly) {
        if (userRole === UserRole.GAO_Supervisor || userRole === UserRole.GAO_Attorney) {
          showSubmit = true;
        } else if (numberOfDays <= 60) {
          showSubmit = true;
        }
      }
    }
    if (showSubmit) {
      nav.push({title:'Submit New Documents',routeToLoad:'/user/submit-new-document/' + protestInfo.a_No, id:'subDoc'});
    }
  }

  private addParties(nav: NavMenu[], userRole: UserRole, protestInfo: ProtestInfo) {
    if (protestInfo.caseCompletionStatus.isZipCreated != true || userRole === UserRole.GAO_Admin) {
      nav.push({title:'Parties',routeToLoad:'/user/parties/' + protestInfo.a_No, id:'parties'});
    }
  }

  private addAdvancedSearch(nav: NavMenu[], userRole: UserRole, protestInfo: ProtestInfo) {
    if (userRole === UserRole.GAO_Admin || userRole === UserRole.GAO_Supervisor || userRole === UserRole.GAO_Attorney) {
      nav.push({title:'Advanced Search',routeToLoad:'/user/advanced-search/' + protestInfo.a_No, id:'advSearch'});
    }
  }

  private addJoinUnjoin(nav: NavMenu[], userRole: UserRole, protestInfo: ProtestInfo) {
    if (protestInfo.caseCompletionStatus.isZipCreated != true && userRole === UserRole.GAO_Admin) {
      nav.push({title:'Join/Unjoin Cases',routeToLoad:'/admin/join-case/' + protestInfo.a_No, id:'joinUnJoinCase'});
    }
  }

  private addEmailPref(nav: NavMenu[], userRole: UserRole, protestInfo: ProtestInfo) {
    if (protestInfo.caseCompletionStatus.isZipCreated != true || userRole === UserRole.GAO_Admin) {
      this.displayEmailPref = true;
    }
  }

  private addReconEntitleCosts(userRole: UserRole, protestInfo: ProtestInfo) {
    let nav : NavMenu[] = [];
    if (protestInfo.case_Status !== CaseStatus.OPEN) {
      if (userRole != UserRole.GAO_Supervisor && userRole != UserRole.GAO_Attorney) {
        nav.push({title:'Request for Reconsideration',routeToLoad:'/user/reconsideration/' + protestInfo.a_No, id:'recon'});
      }

      if (userRole == UserRole.GAO_Admin || userRole == UserRole.Protester || userRole == UserRole.Secondary_Protester) {
        nav.push({title:'Request for Entitlement',routeToLoad:'/user/entitlement/' + protestInfo.a_No, id:'entitle'});
        nav.push({title:'Claim for Costs',routeToLoad:'/user/cost-claim/' + protestInfo.a_No, id:'cost'});
      }
    }
    this.setAdditionalNav(nav);
  }

  private addComplete(nav: NavMenu[], userRole: UserRole, protestInfo: ProtestInfo) {
    const numberOfDays = protestInfo.public_decision_date ? this.differenceInDays(new Date(), new Date(protestInfo.public_decision_date)) : 0;

    if (protestInfo.case_Status !== CaseStatus.OPEN && numberOfDays > 60 &&
        (userRole === UserRole.GAO_Admin || userRole === UserRole.GAO_Supervisor || userRole === UserRole.GAO_Attorney)) {
      if (protestInfo.caseCompletionStatus.isZipCreated === false) {
        //Zip file needs to be created
        this.completeButtonText = CaseCompleteState.Complete;
      } else if (protestInfo.caseCompletionStatus.isZipCreated === true
        && protestInfo.caseCompletionStatus.isDmEntered === false) {
        //enter DM#
        this.completeButtonText = CaseCompleteState.Final;
      } else if (protestInfo.caseCompletionStatus.isZipCreated === true
        && protestInfo.caseCompletionStatus.isDmEntered === true
        && protestInfo.caseCompletionStatus.isDmVerfied === null) {
        //pending DM
        this.completeButtonText = CaseCompleteState.Pending;
      } else if (protestInfo.caseCompletionStatus.isZipCreated === true
        && protestInfo.caseCompletionStatus.isDmEntered === true
        && protestInfo.caseCompletionStatus.isDmVerfied === false) {
        //verify DM
        this.completeButtonText = CaseCompleteState.Verify;
      }
    }
  }

  differenceInDays(date1: Date, date2: Date): number {
    const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
    const diffInTime = Math.abs(date2.getTime() - date1.getTime());
    return Math.round(diffInTime / oneDay);
  }

  onEmailPref() {
    this._emailSub.next();
  }

  onComplete(text : CaseCompleteState) {
    if (this.protestInfo == undefined) return;
    const protestInfo = this.protestInfo;
    this._completeSub.next({text, protestInfo});
  }

}
